import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useMembershipService } from 'services/memberships';
import { Membership as MembershipType } from 'types/membership';
import Button from 'components/Button';
import styles from './Membership.module.scss';

interface VoidMembershipModalProps {
  show: boolean;
  onHide: () => void;
  membership: MembershipType | null;
  onSuccess: (membership: MembershipType) => void;
  onError: (message: string) => void;
}

function formatName(m: MembershipType) {
  const parts = [m.lastName, m.firstName].filter(Boolean);
  if (m.middleName) parts.splice(1, 0, m.middleName);
  return parts.join(', ') || '—';
}

function VoidMembershipModal({
  show,
  onHide,
  membership,
  onSuccess,
  onError,
}: VoidMembershipModalProps) {
  const membershipService = useMembershipService();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      setReason('');
    }
  }, [show]);

  if (!membership) {
    return null;
  }

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const voidedMembership = await membershipService.voidMembership(
        membership.id,
        reason.trim() || undefined,
      );
      onSuccess(voidedMembership);
      onHide();
    } catch (error) {
      onError(
        error instanceof Error ? error.message : 'Failed to delete membership',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          Delete membership
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.voidDetails}>
          <div className={styles.detailRow}>
            <strong>Name:</strong>
            <span>{formatName(membership)}</span>
          </div>
          {membership.email && (
            <div className={styles.detailRow}>
              <strong>Email:</strong>
              <span>{membership.email}</span>
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="voidReason" className={styles.label}>
            Reason for deletion (optional)
          </label>
          <textarea
            id="voidReason"
            placeholder="Enter the reason for deleting this membership..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={styles.textarea}
            rows={4}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button
          type="button"
          variant="outline"
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={isSubmitting}
          className={styles.deleteButton}
        >
          {isSubmitting ? 'Deleting...' : 'Delete membership'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default VoidMembershipModal;
